import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import AuthShell from './AuthShell';
import { EyeToggle } from './Login';
import {
  PASSWORD_MIN,
  friendlyAuthError,
  inputStyle, inputFocusStyle, inputErrorStyle,
  labelStyle, inlineErrorStyle, primaryButtonStyle, linkStyle,
} from './authUtils';
import { colors, spacing, fontStack } from '../design-system/tokens';

/**
 * /reset-password — set a new password using the recovery token from
 * the email link.
 *
 * Token detection:
 *   1. Supabase JS auto-parses the URL hash/query for recovery tokens
 *      and creates a session.
 *   2. We probe `getSession()` and listen for `PASSWORD_RECOVERY` /
 *      `SIGNED_IN` events. If we have a session within ~1.5s, we
 *      consider the link valid.
 *   3. If we never see one, show the "invalid or expired" state.
 *
 * Note: a regular logged-in user landing here will also see the form.
 * That's intentional — they can update their password from this URL too.
 */
export default function ResetPassword() {
  const [tokenStatus, setTokenStatus] = useState('checking'); // 'checking' | 'valid' | 'invalid'
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirm: false });
  const [focus, setFocus] = useState({ password: false, confirm: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  /* ----- Token / session detection ----- */
  useEffect(() => {
    let cancelled = false;

    const probe = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!cancelled && data?.session) setTokenStatus('valid');
      } catch (_) { /* fallthrough */ }
    };

    // Listen for PASSWORD_RECOVERY (fires when Supabase parses the
    // recovery hash) plus SIGNED_IN as a fallback.
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && session) {
        setTokenStatus('valid');
      }
    });

    // Initial probe (handles the case where the session was created
    // before our listener attached) plus a deferred fallback to mark
    // invalid if nothing fired.
    probe();
    const timeout = setTimeout(() => {
      if (!cancelled) {
        setTokenStatus((prev) => (prev === 'checking' ? 'invalid' : prev));
      }
    }, 1500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      listener?.subscription?.unsubscribe?.();
    };
  }, []);

  /* ----- Validation ----- */
  const passwordValid = password.length >= PASSWORD_MIN;
  const confirmValid = confirm.length > 0 && confirm === password;
  const formValid = passwordValid && confirmValid;
  const showPasswordError = touched.password && password.length > 0 && !passwordValid;
  const showConfirmError = touched.confirm && confirm.length > 0 && !confirmValid;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ password: true, confirm: true });
    if (!formValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const { error: e2 } = await supabase.auth.updateUser({ password });
      if (e2) throw e2;
      setSuccess(true);
      setTimeout(() => {
        if (typeof window !== 'undefined') window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setError(friendlyAuthError(err?.message));
      setSubmitting(false);
    }
  };

  /* ----- Render ----- */

  if (tokenStatus === 'checking') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <h1 style={titleStyle}>Verifying link…</h1>
          <p style={subStyle}>Hold on a moment.</p>
        </div>
      </AuthShell>
    );
  }

  if (tokenStatus === 'invalid') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <h1 style={titleStyle}>This link is invalid or has expired</h1>
          <p style={subStyle}>
            Reset links work once and expire after a short window. Request a fresh one to continue.
          </p>
          <a
            href="/forgot-password"
            style={{
              ...primaryButtonStyle(false, false),
              display: 'inline-block',
              marginTop: spacing.lg,
              maxWidth: 280,
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            Request a new link →
          </a>
          <p
            style={{
              margin: `${spacing.lg}px 0 0`,
              fontSize: 14,
              color: colors.textSecondary,
              fontFamily: fontStack,
            }}
          >
            <a href="/login" style={linkStyle}>← Back to sign in</a>
          </p>
        </div>
      </AuthShell>
    );
  }

  if (success) {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <SuccessGlyph />
          <h1 style={titleStyle}>Password updated</h1>
          <p style={subStyle}>Redirecting you to sign in…</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 style={titleStyle}>Set a new password</h1>
      <p style={subStyle}>Choose a strong password you haven't used before.</p>

      <form
        onSubmit={submit}
        noValidate
        style={{ marginTop: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.base }}
      >
        <div>
          <label htmlFor="reset-password" style={labelStyle}>
            New password <span style={{ color: colors.textSecondary, fontWeight: 400 }}>({PASSWORD_MIN}+ characters)</span>
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="reset-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => { setTouched((t) => ({ ...t, password: true })); setFocus((f) => ({ ...f, password: false })); }}
              onFocus={() => setFocus((f) => ({ ...f, password: true }))}
              minLength={PASSWORD_MIN}
              required
              style={{
                ...inputStyle,
                paddingRight: 44,
                ...(focus.password ? inputFocusStyle : null),
                ...(showPasswordError ? inputErrorStyle : null),
              }}
            />
            <EyeToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />
          </div>
          {showPasswordError && (
            <p style={inlineErrorStyle}>Password needs at least {PASSWORD_MIN} characters.</p>
          )}
        </div>

        <div>
          <label htmlFor="reset-confirm" style={labelStyle}>Confirm new password</label>
          <input
            id="reset-confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onBlur={() => { setTouched((t) => ({ ...t, confirm: true })); setFocus((f) => ({ ...f, confirm: false })); }}
            onFocus={() => setFocus((f) => ({ ...f, confirm: true }))}
            required
            style={{
              ...inputStyle,
              ...(focus.confirm ? inputFocusStyle : null),
              ...(showConfirmError ? inputErrorStyle : null),
            }}
          />
          {showConfirmError && <p style={inlineErrorStyle}>Passwords don't match.</p>}
        </div>

        {error && <p style={{ ...inlineErrorStyle, fontSize: 13, marginTop: 0 }}>{error}</p>}

        <button
          type="submit"
          disabled={!formValid || submitting}
          style={primaryButtonStyle(!formValid, submitting)}
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>

      <p
        style={{
          margin: `${spacing.lg}px 0 0`,
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: 13,
          fontFamily: fontStack,
        }}
      >
        Need a fresh link?{' '}
        <a href="/forgot-password" style={linkStyle}>Request one →</a>
      </p>
    </AuthShell>
  );
}

const titleStyle = {
  margin: 0,
  fontSize: 28,
  fontWeight: 600,
  color: colors.textPrimary,
  letterSpacing: '-0.01em',
  fontFamily: fontStack,
};

const subStyle = {
  margin: '6px 0 0',
  fontSize: 16,
  color: colors.textSecondary,
  fontFamily: fontStack,
  lineHeight: 1.5,
};

function SuccessGlyph() {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: 'rgba(111, 207, 151, 0.18)',
        color: 'var(--success)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 18,
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  );
}
